<script lang="ts">
    import {
        type FeedCommentFragment,
        FeedCommentFragmentDoc,
        FeedCommentQueryDocument,
    } from '$gql/graphql.js';
    import { query } from '$lib/apollo/SvelteApollo.js';
    import {
        type CommentData,
        COMMENTS_MAX_PER_PAGE,
        findPage,
        INITIAL_COMMENTS_TO_SHOW,
    } from './Utils';
    import { NetworkStatus } from '@apollo/client/core/core.cjs';
    import map from 'lodash/map';
    import uniqBy from 'lodash/uniqBy';
    import FeedComment from './FeedComment.svelte';
    import FeedCommentLoader from './FeedCommentLoader.svelte';
    import { useFragment } from '$gql/fragment-masking.js';
    import * as t from '$trans';
    import { getContainer } from '$components/Context/ContainerContext';

    const { lang } = getContainer();
    export let comment: FeedCommentFragment;
    export let postId: number;
    export let commentData: CommentData | null = null;

    let showAllReplies = commentData?.parentId == comment.id;

    let initialPage =
        commentData !== null
            ? findPage(COMMENTS_MAX_PER_PAGE, commentData.commentIndex!)
            : 1;

    const { result, setVariables, fetchMore } = query(FeedCommentQueryDocument, {
        variables: {
            id: comment.id,
            page: initialPage,
            maxPerPage: COMMENTS_MAX_PER_PAGE,
        },
        notifyOnNetworkStatusChange: true,
    });

    $: setVariables({
        id: comment.id,
        page: initialPage,
        maxPerPage: COMMENTS_MAX_PER_PAGE,
    });

    $: comments = map($result.data?.comment?.replies?.results, (c) =>
        useFragment(FeedCommentFragmentDoc, c),
    );
    $: loading = $result.loading;
    $: currentPage = $result.data?.comment.replies.pagerInfo.page || 1;
    $: nbPages = $result.data?.comment.replies.pagerInfo.nbPages || 0;
</script>

<div>
    {#if $result.error}
        <div class={'alert alert-danger !py-2'}>{$result.error.message}</div>
    {/if}

    {#if $result.networkStatus !== NetworkStatus.refetch}
        {#each showAllReplies ? comments : comments.slice(0, INITIAL_COMMENTS_TO_SHOW) as comment (comment.id)}
            <FeedComment
                {comment}
                {postId}
                commentData={commentData?.commentId == comment.id ? commentData : null}
            />
        {/each}
    {/if}

    {#if !showAllReplies && comment.repliesCount > 0 && comment.repliesCount > INITIAL_COMMENTS_TO_SHOW && currentPage === 1 && !loading}
        <button
            on:click|preventDefault={() => (showAllReplies = !showAllReplies)}
            class={'btn-reset text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
        >
            {t.feed_post.see_more_comments({}, $lang)}
        </button>
    {/if}

    {#if !loading && currentPage < nbPages && showAllReplies}
        <button
            class={'btn-reset w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
            on:click|preventDefault={() => {
                fetchMore({
                    variables: {
                        id: comment.id,
                        page: currentPage + 1,
                        maxPerPage: COMMENTS_MAX_PER_PAGE,
                    },
                    updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                        let results = [
                            ...previousQueryResult.comment.replies.results,
                            ...fetchMoreResult.comment.replies.results,
                        ];
                        results = uniqBy(results, 'id');
                        fetchMoreResult.comment.replies.results = results;
                        return fetchMoreResult;
                    },
                });
            }}
        >
            {t.feed_post.see_more_replies({}, $lang)}
        </button>
    {/if}

    {#if $result.loading && comment.repliesCount > 0}
        <FeedCommentLoader small={true} />
    {/if}
</div>
